import type { FetchError } from 'ofetch';

const BLANK_OPTION: ChoiceOption = { value: '', text: '' };

const incidentStatusChoices = INCIDENT_STATUSES.map(s => {
  return {
    value: s,
    text: INCIDENT_STATUS_NAMES[s],
  };
});

/**
 * Gets the list of fields for the signature generator form.
 */
const getSignatureFormConfig = (): FieldConfig[] => [
  {
    id: 'division',
    label: 'Division',
    type: 'choice',
    options: [
      { value: 'CCCFPD', text: 'CCCFPD' },
      { value: 'AMR', text: 'AMR' },
    ],
    default: 'CCCFPD',
    required: true,
  },
  {
    id: 'full_name',
    label: 'Full name',
    type: 'text',
    maxLength: 70,
    required: true,
    readonly: true,
  },
  {
    id: 'position',
    label: 'Position / Title',
    type: 'text',
    maxLength: 50,
    required: true,
  },
  {
    id: 'email',
    label: 'Email',
    type: 'email',
    required: true,
    readonly: true,
  },
  {
    id: 'phone',
    label: 'Phone',
    type: 'phoneWithExt',
    required: true,
    // need help text to show valid phone formats
  },
  {
    id: 'mobile',
    label: 'Mobile',
    type: 'phone',
    // need help text to show valid phone formats
  },
  {
    id: 'fax',
    label: 'Fax',
    type: 'phone',
    // need help text to show valid phone formats
  },
  {
    id: 'address_key',
    label: 'Address',
    type: 'address',
    options: [
      { value: 'cccfpd', text: 'CCCFPD' },
    ],
    required: true,
    hidden: true,
  },
  {
    id: 'pronouns',
    label: 'Pronouns',
    type: 'text',
    maxLength: 30,
  },
];

/**
 * Gets the list of fields for the incident form.
 */
const getIncidentFormConfig = (): FormConfig[] => [
  {
    id: 'incident_number',
    label: 'CAD Incident number',
    type: 'text',
    required: true,
  },
  {
    id: 'neris_incident_number',
    label: 'NERIS ID',
    type: 'text',
    required: false,
  },
  {
    id: 'name',
    label: 'Name',
    type: 'text',
    cols: 12,
  },
  {
    id: 'date_occurred',
    label: 'Date',
    type: 'date',
    required: true,
    cols: 6,
  },
  {
    id: 'time_reported',
    label: 'Time',
    type: 'time',
    required: true,
    cols: 6,
  },
  {
    id: 'investigation_status',
    label: 'Investigation status',
    type: 'choice',
    required: true,
    options: incidentStatusChoices,
    cols: 12,
  },
  {
    label: '',
    type: 'fieldset',
    fields: [
      /*
      {
        id: 'coordinates',
        label: 'Coordinates',
        type: 'coordinates',
        cols: 12,
      }, */
      {
        id: 'address',
        label: 'Address',
        type: 'address',
        required: false,
        cols: 12,
      },

    ],
  },
];

/**
 * Asynchronously gets a list of ChoiceOptions from a given resource.
 *
 * @param fetch An asynchronous function that returns a list of model instances
 * @param mapFunc A function passed to Array.map, which is called on the list of
 * model instances. Should return a list of ChoiceOptions.
 * @param prefix An optional ChoiceOption that is added to the beginning of the list
 * as a default option, if provided.
 */
const getChoiceOptionForModel = async <T>(
  fetch: () => Promise<T[]>,
  mapFunc: (item: T) => ChoiceOption,
  prefix?: ChoiceOption,
): Promise<ChoiceOption[]> => {
  const options = [];
  if (prefix) {
    options.push(prefix);
  }
  try {
    const modelInstances = await fetch();
    options.push(...modelInstances.map(mapFunc));
  }
  catch (err) {
    console.error(`Error getting choice options: ${(err as any).message}`);
  }
  return options;
};

/**
 * Gets the general incident field config.
 * Includes a sub-config by default to allow for creating new incidents
 * instead of selecting an existing one.
 */
const getIncidentFieldConfig = async (): Promise<FieldConfig> => {
  const { getAllIncidents, createIncident } = useApi();
  const toastAlerts = useToastAlerts();
  const incidentOptions = await getChoiceOptionForModel(
    async () => (await getAllIncidents({ investigation_status: 'OPEN' })).results,
    reportIncident => ({
      value: reportIncident.id,
      text: getIncidentDisplayName(reportIncident),
    }),
  );

  return {
    id: 'incident',
    label: 'Related Incident',
    type: 'choice',
    clearable: true,
    options: incidentOptions,
    optionForm: {
      // TODO: make sure the current user has permission to do this
      title: 'Create Incident',
      fields: getIncidentFormConfig(),
      createFunc: async data => {
        try {
          const incident = await createIncident(data as unknown as Incident);
          return { value: incident.id, text: incident.incident_number };
        }
        catch (err) {
          toastAlerts.error(`Error creating incident: ${err}`, {
            dismissable: true,
            dismissAfter: 0, // persist
          });
          throw (err as FetchError).data;
        }
      },
    },
  };
};

const getInvestigatorChoiceFieldConfig = async (): Promise<FieldConfig> => {
  const { getInvestigators } = useApi();

  const investigatorOptions = await getChoiceOptionForModel(
    async () => (await getInvestigators()).results,
    investigator => ({
      value: investigator.id,
      text: `${getUserDisplayName(investigator, true)}`,
    }),
  );

  return {
    id: 'investigator',
    label: 'Investigator',
    type: 'choice',
    clearable: true,
    options: investigatorOptions,
  };
};

/**
 * Gets the general resources field.
 */
const getResourcesFieldConfig = async (): Promise<FieldConfig> => {
  const { getAllReportResources } = useApi();
  const reportResourceOptions = await getChoiceOptionForModel(
    async () => (await getAllReportResources()).results,
    reportResource => ({ value: reportResource.id, text: reportResource.resource }),
  );
  return {
    id: 'resources',
    label: 'Resources Committed to the Incident',
    type: 'choice',
    multiple: true,
    clearable: true,
    options: reportResourceOptions,
  };
};

/**
 * Gets the general incident_type field
 */
const getIncidentTypeFieldConfig = async (): Promise<FieldConfig> => {
  const { getAllIncidentTypes } = useApi();
  const incidentTypeOptions = await getChoiceOptionForModel(
    async () => (await getAllIncidentTypes()).results,
    incidentType => ({ value: incidentType.id, text: incidentType.incident_type }),
    BLANK_OPTION,
  );
  return {
    id: 'incident_type',
    label: 'Incident Type',
    type: 'choice',
    clearable: true,
    options: incidentTypeOptions,
  };
};

const incidentReportMediaField: FieldConfig = {
  id: 'media',
  label: 'Photos & Videos',
  type: 'media',
  multiple: true,
  mainValueKey: 'main_image',
};

/**
 * Configure the investigation report as a select list.
 * @returns
 */
// TODO: Configure to retrieve only active investigations on active incidents.
const getInvestigationReportChoiceFieldConfig = async (): Promise<FieldConfig> => {
  const { getAllInvestigationReports } = useApi();
  const investigationOptions = await getChoiceOptionForModel(
    async () => (await getAllInvestigationReports()).results,
    investigationReport => ({
      value: investigationReport.id,
      text: `${getIncidentDisplayName(investigationReport.incident as Incident)} (Inv: ${getInvestigatorDisplayName(investigationReport.investigator)})`,
    }),
  );

  return {
    id: 'investigationreport',
    label: 'Investigation',
    type: 'choice',
    clearable: true,
    options: investigationOptions,
  };
};

const getReviewersChoiceFieldConfig = async () => {
  return {
    id: 'reviewers',
    label: 'Reviewers',
    type: 'user',
    multiple: true,
    filters: {
      role: (await useReviewerRoleFor('InvestigationReport'))?.id,
    },
  };
};

const getCrimeClassificationOptions = (): ChoiceOption[] => {
  return [
    { value: 'DOC', text: 'Documentation Only' },
    { value: '187', text: '187 PC Homicide' },
    { value: '451(a)', text: '451(a) PC Arson with Great Bodily Injury' },
    { value: '451(b)', text: '451(b) PC Arson of an Inhabited Structure' },
    { value: '451(c)', text: '451(c) PC Arson of a Structure (or Forest Land)' },
    { value: '451(d)', text: '451(d) PC Arson of a Property' },
    { value: '452(a)', text: '452(a) Unlawful Fire with Great Bodily Injury' },
    { value: '452(b)', text: '452(b) Unlawful Fire of an Inhabited Structure' },
    { value: '452(c)', text: '452(c) Unlawful Fire of a Structure (or Forest Land)' },
    { value: '452(d)', text: '452(d) Unlawful Fire of a Property' },
    { value: '453(a)', text: '453(a) Possession of an Incendiary Device (or Material)' },
    { value: '455', text: '455 PC Attempted Arson' },
    { value: '12303', text: '12303 PC Possession of a Destructive Device' },
    { value: '12305', text: '12305 H&S Possession of Explosives' },
    { value: '44-2.002', text: '44-2.002 CCCO 72-19 Possession of Fireworks' },
    { value: '307.1.1', text: '307.1.1 CFC Burning Prohibited' },
    { value: '148.4(a)', text: '148.4(a) PC Tampering with Fire Protection Equipment' },
    { value: '148.4(b)', text: '148.4(b) (Felony) PC Tampering with Fire Protection Equipment (or False Alarm)' },
    { value: 'OTHER', text: 'Other (see description)' },
  ];
};

const getCrimeTypeOptions = async (): Promise<ChoiceOption[]> => {
  return [
    { value: 'FELONY', text: 'Felony' },
    { value: 'MISDEMEANOR', text: 'Misdemeanor' },
    { value: 'OTHER', text: 'Other' },
  ];
};

/**
 * Returns a map of functions for retrieving lists of fields for various forms.
 */
export const useFieldConfigs = () => ({
  incidentReportMediaField,
  getChoiceOptionForModel,
  getIncidentFieldConfig,
  getCrimeClassificationOptions,
  getCrimeTypeOptions,
  getInvestigationReportChoiceFieldConfig,
  getResourcesFieldConfig,
  getReviewersChoiceFieldConfig,
  getSignatureFormConfig,
  getIncidentFormConfig,
  getInvestigatorChoiceFieldConfig,
});
